import { useState } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { uploadFile } from "../lib/api";
import { toast } from "sonner";

const base = "w-full rounded-md border border-line bg-white px-4 py-3 text-ink placeholder:text-muted2/70 focus:outline-none focus:ring-2 focus:ring-ice/40 focus:border-ice transition-[border-color,box-shadow]";

export function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-ink mb-2">{label}{required && <span className="text-ice"> *</span>}</span>
      {children}
      {hint && <span className="block text-xs text-muted2 mt-1.5">{hint}</span>}
    </label>
  );
}

export const Input = (props) => <input {...props} className={base} />;
export const Textarea = (props) => <textarea rows={5} {...props} className={base} />;
export const Select = ({ options, ...props }) => (
  <select {...props} className={base}>
    <option value="">Sélectionner…</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export function ChoiceGrid({ options, value, onChange, name }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          data-testid={`choice-${name}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
          onClick={() => onChange(o)}
          className={`text-left rounded-md border px-4 py-3.5 font-semibold transition-[border-color,background-color] ${value === o ? "border-ice bg-ice/5 text-ink" : "border-line bg-white text-maintext hover:border-ice/40"}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function FileUpload({ files, setFiles, testid = "file-upload" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    setBusy(true);
    try {
      for (const f of list) {
        const res = await uploadFile(f);
        setFiles((prev) => [...prev, { name: f.name, url: res.url, path: res.path }]);
      }
    } catch {
      toast.error("Échec de l’envoi du fichier (formats : jpg, png, webp, pdf — max 10 Mo).");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label data-testid={testid} className="flex items-center justify-center gap-3 rounded-md border-2 border-dashed border-line bg-cloud px-4 py-6 cursor-pointer hover:border-ice/50 transition-colors">
        {busy ? <Loader2 className="animate-spin text-ice" size={20} /> : <UploadCloud className="text-ice" size={22} />}
        <span className="text-sm font-semibold text-maintext">{busy ? "Envoi…" : "Ajouter des photos ou plans (jpg, png, webp, pdf)"}</span>
        <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handle} />
      </label>
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2 mt-3">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 bg-white border border-line rounded px-3 py-1.5 text-sm text-ink">
              {f.name}
              <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-muted2 hover:text-ink"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Consent({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="rgpd-consent" className="mt-1 w-4 h-4 accent-[#0284C7]" />
      <span className="text-sm text-maintext leading-relaxed">
        J’accepte que mes informations soient utilisées pour traiter ma demande, conformément à la <a href="/politique-de-confidentialite" className="text-ice font-semibold underline">politique de confidentialité</a>.
      </span>
    </label>
  );
}
